import { useMatchRoute } from '@tanstack/react-router'
import { type ReactElement, type ReactNode, use, useCallback, useContext, useMemo, useState } from 'react'

import { Dialog } from '@orus.eu/pharaoh-next/components/patterns/dialog'

import { HelpContactContext } from './help-contact-hooks.ts'
import { HelpContactPopup } from './help-contact-popup.tsx'

import { PharaohNextScope } from '#/lib/pharaoh-next/pharaoh-next-scope.tsx'

type HelpContactRootProps = {
  children: ReactNode
}

function HelpContactRoot({ children }: HelpContactRootProps): ReactElement {
  const parentContext = useContext(HelpContactContext)
  return parentContext ? <>{children}</> : <HelpContactRootOwner>{children}</HelpContactRootOwner>
}

function HelpContactRootOwner({ children }: HelpContactRootProps): ReactElement {
  const [handle] = useState(() => Dialog.createHandle())
  const [open, setOpen] = useState(handle.isOpen)
  const matchRoute = useMatchRoute()
  const openHelpContact = useCallback((): void => handle.open(null), [handle])
  const contextValue = useMemo(() => ({ handle, openHelpContact }), [handle, openHelpContact])
  const surface = matchRoute({ to: '/bo', fuzzy: true, includeSearch: false }) ? 'bo' : 'fo'

  return (
    <HelpContactContext value={contextValue}>
      <>
        {children}
        <Dialog.Root handle={handle} onOpenChange={setOpen}>
          <div className="relative z-[1300]">
            <PharaohNextScope surface={surface}>
              <HelpContactPopup open={open} />
            </PharaohNextScope>
          </div>
        </Dialog.Root>
      </>
    </HelpContactContext>
  )
}

type HelpContactTriggerProps = Omit<Dialog.Trigger.Props, 'handle'>

function HelpContactTrigger(props: HelpContactTriggerProps): ReactElement {
  const context = use(HelpContactContext)
  if (context === undefined) throw new Error('HelpContact.Trigger must be used within HelpContact.Root')
  return <Dialog.Trigger {...props} handle={context.handle} />
}

const HelpContact = {
  Root: HelpContactRoot,
  Trigger: HelpContactTrigger,
}

export { HelpContact }
